import { faSave, faSyncAlt, faUndo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import { Button, Col, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap';
import { createExchangeRate } from '../../../helpers/api/coins';
import { fetchLiveExchangeRate } from '../../../helpers/api/currency';
import { SUCCESS_RESPONSE } from '../../../helpers/codeResponses/codes';
import { formatAmountForBackend } from '../../../helpers/currencyConversion';
import handleErrorMessage from '../../../helpers/errors/handleErrorMessage';
import Flex from '../../common/Flex';
import ButtonIcon from '../../common/ButtonIcon';
import { Toast } from '../../UI/toast/Toast';

const CreateExhangeRateModal = ({
    isOpen,
    setIsOpen,
    exchangeRate,
    currency = { name: '', iso: '', id: null },
    baseCurrency = { iso: 'USD', symbol: '$' },
    mutate = () => { },
    condominiumId = null,
    suggestedValue = null,
    isToday = false,
}) => {
    const toggle = () => setIsOpen(!isOpen);

    const [val, setVal] = useState(0);
    const today = new Date().toISOString().slice(0, 10);
    const [date, setDate] = useState(exchangeRate?.date ?? today);
    const [submitting, setSubmitting] = useState(false);
    const [isFetchingLive, setIsFetchingLive] = useState(false);
    const [prefillSource, setPrefillSource] = useState(null); // 'live' | 'suggested' | null

    // Trae la tasa oficial: si `date` es hoy, la tasa vigente (DolarApi); si
    // es una fecha pasada, se consulta el histórico real de ese día (fuente
    // separada, solo para esta consulta puntual — el cron sigue usando
    // únicamente DolarApi). Si no hay dato para esa fecha (muy antigua, por
    // ejemplo), cae en `suggestedValue` como respaldo editable.
    const fetchLive = useCallback(async () => {
        if (!condominiumId) return;
        setIsFetchingLive(true);
        try {
            const { value } = await fetchLiveExchangeRate({ condominium_id: condominiumId, date });
            setVal(value);
            setPrefillSource('live');
        } catch (error) {
            if (suggestedValue) {
                setVal(suggestedValue);
                setPrefillSource('suggested');
            } else {
                setPrefillSource(null);
            }
        } finally {
            setIsFetchingLive(false);
        }
    }, [condominiumId, date, suggestedValue]);

    useEffect(() => {
        if (!isOpen || exchangeRate?.value) return;

        fetchLive();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const closeBtn = <>
        <Button className="close font-weight-normal bg-transparent border-0 shadow-none" onClick={toggle}>
            &times;
        </Button>
    </>

    const save = async () => {
        setSubmitting(true);
        createExchangeRate({ currency: currency.id, exchangeRate: { value: formatAmountForBackend(val), date } })
            .then(({ code }) => {
                if (code === SUCCESS_RESPONSE.OK) {
                    Toast.fire({ type: 'success', title: 'Tasa de cambio creada exitosamente.' });
                    mutate();
                    toggle();
                    setVal(0);
                    setDate(today);
                    setPrefillSource(null);
                }
            })
            .catch(error => handleErrorMessage(error))
            .finally(() => setSubmitting(false));
    }

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered modalClassName="theme-modal">
            <form>
                <ModalHeader toggle={toggle} tag="div" className="px-card bg-light border-0 flex-between-center" close={closeBtn}>
                    <h5 className='mb-0'>{exchangeRate?.value ? 'Edición de tasa de cambio' : 'Registro de tasa de cambio'}</h5>
                </ModalHeader>
                <ModalBody>
                    <Row form>
                        <Col tag={FormGroup}>
                            <Label>Moneda a convertir</Label>
                            <Label className='form-control'>
                                {currency?.iso} | {currency?.name}
                            </Label>
                        </Col>
                    </Row>

                    <Row form>
                        <Col tag={FormGroup}>
                            <Flex align='center' justify='between'>
                                <Label className='mb-0'>Tasa de cambio al día por <b>{baseCurrency?.symbol || '$'} 1</b></Label>
                                {condominiumId && (
                                    <Button
                                        color='link'
                                        size='sm'
                                        className='p-0'
                                        disabled={isFetchingLive}
                                        onClick={fetchLive}
                                        title={isToday ? "Volver a consultar la tasa oficial de hoy" : "Volver a consultar el histórico de esa fecha"}
                                    >
                                        {isFetchingLive ? <Spinner size='sm' /> : <FontAwesomeIcon icon={faSyncAlt} />}
                                    </Button>
                                )}
                            </Flex>
                            <NumberFormat
                                placeholder="Ingrese la tasa de cambio actualizada al día."
                                prefix={`${currency?.iso} `}
                                onValueChange={({ value }) => setVal(value)}
                                value={val}
                                thousandSeparator
                                decimalScale={2}
                                fixedDecimalScale
                                className='form-control'
                            />
                            {!val && <span className='text-danger fs--1 mt-2'>El campo es obligatorio.</span>}
                            {prefillSource === 'live' && (
                                <span className='text-success fs--1 mt-2 d-block'>
                                    {isToday
                                        ? 'Tasa oficial obtenida automáticamente para hoy. Puedes ajustarla antes de guardar.'
                                        : 'Tasa oficial histórica obtenida automáticamente para esta fecha. Puedes ajustarla antes de guardar.'}
                                </span>
                            )}
                            {prefillSource === 'suggested' && (
                                <span className='text-warning fs--1 mt-2 d-block'>
                                    Sugerencia: última tasa registrada. No se encontró histórico para esta fecha, ajusta el valor si corresponde a otro.
                                </span>
                            )}
                        </Col>
                    </Row>

                    <Row form className="mt-2">
                        <Col tag={FormGroup}>
                            <Label>Fecha de la tasa</Label>
                            <input
                                type="date"
                                className="form-control"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                            />
                            {/* Sin `max`: un pago puede registrarse a una fecha futura (p.ej.
                                un pago programado), y para esa fecha no existe todavía una
                                tasa oficial que consultar — el admin necesita poder declarar
                                manualmente la tasa estimada para esa fecha. */}
                            {!date && <span className='text-danger fs--1 mt-2'>La fecha es obligatoria.</span>}
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Flex align='center' justify='end'>
                        <ButtonIcon icon={faUndo} onClick={toggle} color='falcon-default' size='md' className='px-5 mr-2' transform="down-1 shrink-4" ><span>Volver</span></ButtonIcon>
                        <Button color='primary' onClick={save} size='md' disabled={submitting || !val || !date} className='px-5 mr-2' transform="down-1 shrink-4" >
                            {!submitting ? <FontAwesomeIcon icon={faSave} className='mr-2' /> : <Spinner size='sm' className='mr-2' />}
                            <span>{exchangeRate?.value ? 'Actualizar tasa de cambio' : 'Registrar tasa de cambio'}</span>
                        </Button>
                    </Flex>
                </ModalFooter>
            </form>
        </Modal>
    )
}

export default CreateExhangeRateModal;